import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';

/**
 * AD ASTRA side navigation drawer — open/close, scroll lock, focus trap,
 * return focus to the header menu trigger on close.
 *
 * @typedef {object} NavDrawerRefs
 * @property {HTMLDetailsElement} details
 * @property {HTMLElement} panel
 * @property {HTMLElement} trigger
 *
 * @extends {Component<NavDrawerRefs>}
 */
class NavDrawerComponent extends Component {
  requiredRefs = ['details', 'panel', 'trigger'];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keyup', this.#onKeyUp);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
  }

  /** @param {KeyboardEvent} event */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape' || !this.isOpen) return;
    this.close();
  };

  get isOpen() {
    return this.refs.details.classList.contains('menu-open');
  }

  /**
   * @param {Event} [event]
   */
  toggle(event) {
    event?.preventDefault();
    return this.isOpen ? this.close() : this.open();
  }

  open() {
    const { details, trigger, panel } = this.refs;

    trigger.setAttribute('aria-expanded', 'true');
    details.setAttribute('open', '');
    details.classList.add('menu-open');

    requestAnimationFrame(() => {
      onAnimationEnd(panel, () => trapFocus(panel), { subtree: false });
    });
  }

  /**
   * @param {Event} [event]
   */
  close(event) {
    event?.preventDefault();
    const { details, trigger, panel } = this.refs;

    trigger.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    onAnimationEnd(
      panel,
      () => {
        details.removeAttribute('open');
        removeTrapFocus();
        trigger.focus();

        const nested = this.querySelectorAll('.nav-drawer__accordion[open]');
        nested.forEach((el) => {
          if (el instanceof HTMLDetailsElement) el.removeAttribute('open');
        });
      },
      { subtree: false }
    );
  }
}

if (!customElements.get('nav-drawer-component')) {
  customElements.define('nav-drawer-component', NavDrawerComponent);
}
